import React, { useEffect } from 'react';
import styles from './ZakatInfoModal.module.css';

const CONTENT = {
  zakatDate: {
    title: 'Zakat Year-End Date',
    body: (
      <>
        <p>Your <strong>Zakat year-end date</strong> (Hawl) is the date your lunar year of ownership completes. Zakat becomes due once you have held wealth above the Nisab threshold for a full lunar year.</p>
        <p>If you are unsure, use the date you intend to pay Zakat — typically the same date each year (e.g. the 1st of Ramadan).</p>
        <p>We use this date to look up the closing price of each stock so the calculation reflects what your holdings were worth on that exact day.</p>
      </>
    ),
  },

  investorType: {
    title: 'Trader vs Long-Term Investor',
    body: (
      <>
        <div className={styles.infoSection}>
          <p className={styles.infoSectionTitle}>↗ Day Trader / Active Trader</p>
          <p>You buy and sell stocks regularly with the intention of profit from price movements. Scholars treat these holdings the same as <em>trade goods</em> — Zakat is 2.5% of the <strong>full market value</strong> of all shares held on your Zakat date.</p>
          <p className={styles.infoSource}>Ibn Qudamah — Al-Mughni · AAOIFI Zakat Standard</p>
        </div>
        <div className={styles.infoSection}>
          <p className={styles.infoSectionTitle}>◎ Long-Term Investor</p>
          <p>You hold shares for months or years to benefit from dividends and growth. Since you are not trading the shares themselves, scholars hold that Zakat applies only to the <em>zakatable portion</em> of the underlying company assets — not the full market price. Three valid methods exist for calculating that portion.</p>
          <p className={styles.infoSource}>Mufti Taqi Usmani · AAOIFI Standard No. 35 · Ibn Baz (ra)</p>
        </div>
      </>
    ),
  },

  methods: {
    title: 'Zakat Calculation Methods',
    body: (
      <>
        <p className={styles.infoIntro}>All three long-term methods are recognised scholarly opinions. Choose the one your scholar or community follows.</p>

        <div className={styles.infoSection}>
          <p className={styles.infoSectionTitle}>Simple & Safe — 2.5% of market value</p>
          <p>Pay 2.5% on the full market value of your shares on the Zakat date. The most conservative approach — you always pay at least as much as required.</p>
          <p className={styles.infoFormula}>Zakat = Price × Shares × 2.5%</p>
          <p className={styles.infoSource}>Ibn Baz (ra) · Ibn Uthaymeen (ra)</p>
        </div>

        <div className={styles.infoSection}>
          <p className={styles.infoSectionTitle}>25% Rule <span className={styles.recTag}>Recommended</span></p>
          <p>Approximately 25% of a company's market value represents its liquid zakatable assets (cash, receivables). This is widely used by contemporary scholars as a practical middle ground.</p>
          <p className={styles.infoFormula}>Zakat = Price × Shares × 25% × 2.5%</p>
          <p className={styles.infoSource}>Dr. Yusuf al-Qaradawi · Dr. Wahbah al-Zuhayli · AAOIFI</p>
        </div>

        <div className={styles.infoSection}>
          <p className={styles.infoSectionTitle}>Precise Asset % — Custom percentage</p>
          <p>You specify the exact zakatable percentage, ideally sourced from the company's latest published Zakat factsheet or annual report. If you don't have the figure, 25% is a safe estimate.</p>
          <p className={styles.infoFormula}>Zakat = Price × Shares × Your% × 2.5%</p>
          <p className={styles.infoSource}>AAOIFI Shariah Standard No. 35 · OIC Fiqh Academy</p>
        </div>

        <div className={styles.infoSection}>
          <p className={styles.infoSectionTitle}>CRI Method — Balance sheet calculation</p>
          <p>The most precise method. Zakat is based on each share's pro-rata ownership of the company's <strong>Cash + Receivables + Inventory</strong> from the latest annual balance sheet — the three categories that attract Zakat.</p>
          <p className={styles.infoFormula}>Zakatable/share = (Cash + Receivables + Inventory) ÷ Shares Outstanding<br />Zakat = Zakatable/share × Your Shares × 2.5%</p>
          <p className={styles.infoSource}>Mufti Taqi Usmani · AAOIFI Standard No. 35</p>
        </div>
      </>
    ),
  },
};

export default function ZakatInfoModal({ topic, onClose }) {
  const content = CONTENT[topic];

  // Close on Escape — must be before any early return
  useEffect(() => {
    if (!content) return;
    function handler(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [content, onClose]);

  if (!content) return null;

  return (
    <div className={styles.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{content.title}</h3>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className={styles.modalBody}>
          {content.body}
        </div>
      </div>
    </div>
  );
}
